import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import {
    fetchPreferences,
    getPreference,
    removePreference,
    setPreference,
    USER_PREFERENCES_QUERY_KEY,
} from 'App/contexts/UserPreferences.utils';
import UserPreferencesMigrator from 'App/components/UserPreferencesMigrator';
import PropTypes from 'prop-types';

const UserPreferencesContext = createContext({
    preferences: {},
    isLoading: true,
    getPreference: () => null,
    setPreference: () => {},
    removePreference: () => {},
});

function UserPreferencesProvider({ children }) {
    const queryClient = useQueryClient();

    const { data: preferences = {}, isLoading } = useQuery(USER_PREFERENCES_QUERY_KEY, {
        queryFn: fetchPreferences,
        staleTime: Infinity,
        cacheTime: Infinity,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchInterval: false,
    });

    const handleMigrated = useCallback(
        (migratedEntries) => {
            // Seed the cache so freshly migrated values are readable immediately
            // (consumers read preferences once, at mount).
            queryClient.setQueryData(USER_PREFERENCES_QUERY_KEY, (old = {}) => {
                const next = { ...old };
                migratedEntries.forEach(({ name, value }) => {
                    next[name] = { ...next[name], name, value };
                });
                return next;
            });
            // Refetch in the background to pick up server-assigned ids.
            queryClient.invalidateQueries(USER_PREFERENCES_QUERY_KEY);
        },
        [queryClient]
    );

    const contextValue = useMemo(
        () => ({
            preferences,
            isLoading,
            getPreference,
            setPreference,
            removePreference,
        }),
        [preferences, isLoading]
    );

    return (
        <UserPreferencesContext.Provider value={contextValue}>
            <UserPreferencesMigrator preferences={preferences} isLoading={isLoading} onMigrated={handleMigrated}>
                {children}
            </UserPreferencesMigrator>
        </UserPreferencesContext.Provider>
    );
}

UserPreferencesProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

function useUserPreferences() {
    return useContext(UserPreferencesContext);
}

export { USER_PREFERENCES_QUERY_KEY, UserPreferencesContext, UserPreferencesProvider, useUserPreferences };
